'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import type { ReactNode } from 'react'
import { useActionToast } from '@mentora/app-shared/i18n/use-action-toast'
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch, Tabs, TabsContent, TabsList, TabsTrigger } from '@mentora/ui-components'
import { Award, Pencil, Plus, Tag, Trash2, Upload } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState, useTransition } from 'react'

interface CategoryItem {
  id: string
  slug: string
  name: string
}

interface CouponItem {
  id: string
  organizationId: string
  courseId: string | null
  courseTitle: string | null
  code: string
  discountPercent: string
  scope: 'all' | 'course'
  maxUses: number | null
  active: boolean
  usageCount: number
  createdAt: string
}

interface OrgCourseOption {
  id: string
  title: string
}

interface CouponFormState {
  code: string
  discountPercent: string
  scope: 'all' | 'course'
  courseId: string
  maxUses: string
  active: boolean
}

const EMPTY_COUPON_FORM: CouponFormState = {
  code: '',
  discountPercent: '',
  scope: 'all',
  courseId: '',
  maxUses: '',
  active: true,
}

/** The coupon payload both the create and the update action take. */
export interface CouponInput {
  code: string
  discountPercent: number
  scope: 'all' | 'course'
  courseId?: string
  maxUses?: number | null
  active?: boolean
}

/**
 * The six server actions the panel drives.
 *
 * The panel owns the whole settings screen — three tabs, five dialogs, the
 * certificate upload — and knows nothing about which storefront it is rendering
 * for. Each app passes its own actions, which is the only thing the two copies
 * of this screen ever differed on.
 */
export interface SettingsActions {
  createCategory: (input: { name: string }) => Promise<ActionResult<{ id: string, name: string, slug: string }>>
  renameCategory: (input: { id: string, name: string }) => Promise<ActionResult>
  deleteCategory: (input: { id: string }) => Promise<ActionResult>
  createCoupon: (input: CouponInput) => Promise<ActionResult<{ id: string }>>
  updateCoupon: (input: CouponInput & { id: string }) => Promise<ActionResult>
  deleteCoupon: (input: { id: string }) => Promise<ActionResult>
}

/** The active organization's own settings: categories, coupons and the certificate. */
export interface OrgSettings {
  categories: CategoryItem[]
  coupons: CouponItem[]
  orgCourses: OrgCourseOption[]
  actions: SettingsActions
}

/** A storefront-specific tab, rendered after the organization's own. */
export interface SettingsTab {
  value: string
  label: string
  content: ReactNode
}

export interface SettingsPanelProps {
  /** null when no organization is selected — an app admin's "All organizations". */
  org: OrgSettings | null
  /**
   * localStorage namespace for the certificate template, e.g. `ascps`. The
   * storefronts do not agree on this — see each app's binding.
   */
  storageKeyPrefix: string
  /** E.g. ASCPS' membership price, which only app admins are given. */
  extraTabs?: SettingsTab[]
  /** The tab to open on (`?tab=`), when the screen has it. */
  initialTab?: string
}

const ORG_TABS = ['categories', 'coupons', 'certificate'] as const

/**
 * The one /settings screen: the selected organization's tabs, then whatever
 * storefront-wide tabs the app hands in. Each tab's own gate is the app's
 * concern — this only renders what it is given.
 */
export function SettingsPanel({ org, storageKeyPrefix, extraTabs = [], initialTab }: SettingsPanelProps) {
  const t = useTranslations('settings')
  const tabs = [...(org ? ORG_TABS : []), ...extraTabs.map(tab => tab.value)]
  const defaultTab = initialTab && tabs.includes(initialTab) ? initialTab : tabs[0]

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8 sm:px-8">
        <div>
          <h1 className="font-(--font-display) text-3xl tracking-tight text-foreground">
            {t('title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
        </div>

        <Tabs defaultValue={defaultTab}>
          <TabsList>
            {org && (
              <>
                <TabsTrigger value="categories">{t('tabs.categories')}</TabsTrigger>
                <TabsTrigger value="coupons">{t('tabs.coupons')}</TabsTrigger>
                <TabsTrigger value="certificate">{t('tabs.certificate')}</TabsTrigger>
              </>
            )}
            {extraTabs.map(tab => (
              <TabsTrigger key={tab.value} value={tab.value}>{tab.label}</TabsTrigger>
            ))}
          </TabsList>

          {org && <OrgSettingsTabs {...org} storageKeyPrefix={storageKeyPrefix} />}
          {extraTabs.map(tab => (
            <TabsContent key={tab.value} value={tab.value} className="mt-4">
              {tab.content}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  )
}

/** The organization's three tabs and their dialogs, inside `SettingsPanel`'s `Tabs`. */
function OrgSettingsTabs({
  categories,
  coupons,
  orgCourses,
  storageKeyPrefix,
  actions,
}: OrgSettings & { storageKeyPrefix: string }) {
  const t = useTranslations('settings')
  const tCommon = useTranslations('common')
  const { showError, showSuccess } = useActionToast()
  const [isPending, startTransition] = useTransition()

  // Derived, not constant: the two storefronts wrote under different prefixes
  // before this component was shared, and the keys name data already sitting in
  // users' browsers.
  const templateKey = `${storageKeyPrefix}.certificateTemplate`
  const templateNameKey = `${templateKey}.name`

  // ── Category add dialog ──
  const [addCatOpen, setAddCatOpen] = useState(false)
  const [addCatName, setAddCatName] = useState('')

  // ── Category rename dialog ──
  const [renameCat, setRenameCat] = useState<CategoryItem | null>(null)
  const [renameCatName, setRenameCatName] = useState('')

  // ── Category delete dialog ──
  const [deleteCat, setDeleteCat] = useState<CategoryItem | null>(null)

  // ── Coupon dialogs ──
  const [couponDialogOpen, setCouponDialogOpen] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState<CouponItem | null>(null)
  const [couponForm, setCouponForm] = useState<CouponFormState>(EMPTY_COUPON_FORM)
  const [deleteCoupon, setDeleteCoupon] = useState<CouponItem | null>(null)

  // ── Certificate template (mock — stored client-side in localStorage) ──
  const [templateDataUrl, setTemplateDataUrl] = useState<string | null>(null)
  const [templateName, setTemplateName] = useState<string | null>(null)
  const [templateError, setTemplateError] = useState<string | null>(null)
  const templateInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined')
      return
    setTemplateDataUrl(window.localStorage.getItem(templateKey))
    setTemplateName(window.localStorage.getItem(templateNameKey))
  }, [templateKey, templateNameKey])

  function onTemplateFile(e: React.ChangeEvent<HTMLInputElement>) {
    setTemplateError(null)
    const file = e.target.files?.[0]
    if (!file)
      return
    if (!file.type.startsWith('image/')) {
      setTemplateError(t('certificate.errors.invalidType'))
      e.target.value = ''
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setTemplateError(t('certificate.errors.tooLarge'))
      e.target.value = ''
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result as string
      window.localStorage.setItem(templateKey, dataUrl)
      window.localStorage.setItem(templateNameKey, file.name)
      setTemplateDataUrl(dataUrl)
      setTemplateName(file.name)
      showSuccess(t('certificate.toasts.uploaded'))
    }
    reader.onerror = () => setTemplateError(t('certificate.errors.readFailed'))
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  function onRemoveTemplate() {
    window.localStorage.removeItem(templateKey)
    window.localStorage.removeItem(templateNameKey)
    setTemplateDataUrl(null)
    setTemplateName(null)
    showSuccess(t('certificate.toasts.removed'))
  }

  function run(action: () => Promise<{ ok: boolean } & Record<string, unknown>>, successMsg: string, onDone?: () => void) {
    startTransition(async () => {
      const result = await action()
      if (!result.ok) {
        showError(result as Parameters<typeof showError>[0])
        return
      }
      showSuccess(successMsg)
      onDone?.()
    })
  }

  function onAddCategory(e: React.FormEvent) {
    e.preventDefault()
    run(
      () => actions.createCategory({ name: addCatName }),
      t('categories.toasts.created'),
      () => {
        setAddCatOpen(false)
        setAddCatName('')
      },
    )
  }

  function onRenameCategory(e: React.FormEvent) {
    e.preventDefault()
    if (!renameCat)
      return
    run(
      () => actions.renameCategory({ id: renameCat.id, name: renameCatName }),
      t('categories.toasts.renamed'),
      () => setRenameCat(null),
    )
  }

  function onDeleteCategory() {
    if (!deleteCat)
      return
    run(
      () => actions.deleteCategory({ id: deleteCat.id }),
      t('categories.toasts.deleted'),
      () => setDeleteCat(null),
    )
  }

  function openCouponDialog(c?: CouponItem) {
    if (c) {
      setEditingCoupon(c)
      setCouponForm({
        code: c.code,
        discountPercent: c.discountPercent,
        scope: c.scope,
        courseId: c.courseId ?? '',
        maxUses: c.maxUses != null ? String(c.maxUses) : '',
        active: c.active,
      })
    }
    else {
      setEditingCoupon(null)
      setCouponForm(EMPTY_COUPON_FORM)
    }
    setCouponDialogOpen(true)
  }

  function onSubmitCoupon(e: React.FormEvent) {
    e.preventDefault()
    const discountNum = Number.parseFloat(couponForm.discountPercent)
    const maxUsesNum = couponForm.maxUses.trim()
      ? Number.parseInt(couponForm.maxUses, 10)
      : null

    if (!Number.isFinite(discountNum) || discountNum <= 0 || discountNum > 100) {
      showError({ ok: false, errorKey: 'settings.coupons.errors.discountInvalid' })
      return
    }
    if (maxUsesNum !== null && (!Number.isFinite(maxUsesNum) || maxUsesNum <= 0)) {
      showError({ ok: false, errorKey: 'settings.coupons.errors.maxUsesInvalid' })
      return
    }

    const payload = {
      code: couponForm.code.trim().toUpperCase(),
      discountPercent: discountNum,
      scope: couponForm.scope,
      courseId: couponForm.scope === 'course' ? couponForm.courseId : undefined,
      maxUses: maxUsesNum,
      active: couponForm.active,
    }

    run(
      async () => editingCoupon
        ? actions.updateCoupon({ id: editingCoupon.id, ...payload })
        : actions.createCoupon(payload),
      editingCoupon
        ? t('coupons.toasts.updated')
        : t('coupons.toasts.created'),
      () => {
        setCouponDialogOpen(false)
        setEditingCoupon(null)
        setCouponForm(EMPTY_COUPON_FORM)
      },
    )
  }

  function onDeleteCoupon() {
    if (!deleteCoupon)
      return
    run(
      () => actions.deleteCoupon({ id: deleteCoupon.id }),
      t('coupons.toasts.deleted'),
      () => setDeleteCoupon(null),
    )
  }

  function formatPercent(value: string): string {
    const n = Number.parseFloat(value)
    if (!Number.isFinite(n))
      return value
    return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}%`
  }

  return (
    <>

      {/* ── Categories ─────────────────────────────── */}
      <TabsContent value="categories" className="mt-4">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {categories.length === 0
                ? t('categories.emptySubtitle')
                : `${categories.length} ${categories.length === 1 ? t('tabs.categories').toLowerCase().slice(0, -1) : t('tabs.categories').toLowerCase()}`}
            </p>
            <Button size="sm" onClick={() => setAddCatOpen(true)}>
              <Plus className="size-4" />
              {t('categories.addButton')}
            </Button>
          </div>

          {categories.length === 0
            ? (
                <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border/70 py-12 text-center">
                  <p className="font-medium text-foreground">{t('categories.emptyTitle')}</p>
                  <p className="text-sm text-muted-foreground">{t('categories.emptySubtitle')}</p>
                </div>
              )
            : (
                <div className="divide-y divide-border rounded-lg border border-border/60">
                  {categories.map(cat => (
                    <div
                      key={cat.id}
                      className="flex items-center justify-between gap-3 px-4 py-3"
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">{cat.name}</p>
                        <p className="text-xs text-muted-foreground">{cat.slug}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={isPending}
                          onClick={() => {
                            setRenameCat(cat)
                            setRenameCatName(cat.name)
                          }}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={isPending}
                          onClick={() => setDeleteCat(cat)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
        </div>
      </TabsContent>

      {/* ── Coupons ───────────────────────────────── */}
      <TabsContent value="coupons" className="mt-4">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {coupons.length === 0
                ? t('coupons.emptySubtitle')
                : t('coupons.count', { count: coupons.length })}
            </p>
            <Button size="sm" onClick={() => openCouponDialog()}>
              <Plus className="size-4" />
              {t('coupons.addButton')}
            </Button>
          </div>

          {coupons.length === 0
            ? (
                <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border/70 py-12 text-center">
                  <Tag className="size-10 text-muted-foreground/60" />
                  <p className="font-medium text-foreground">{t('coupons.emptyTitle')}</p>
                  <p className="text-sm text-muted-foreground">{t('coupons.emptySubtitle')}</p>
                </div>
              )
            : (
                <div className="divide-y divide-border rounded-lg border border-border/60">
                  {coupons.map(c => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between gap-3 px-4 py-3"
                    >
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-sm font-medium text-foreground">
                            {c.code}
                          </span>
                          <Badge variant={c.active ? 'default' : 'secondary'} className="text-xs">
                            {c.active ? t('coupons.statusActive') : t('coupons.statusInactive')}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {c.scope === 'course'
                              ? c.courseTitle ?? t('coupons.scopeCourseUnknown')
                              : t('coupons.scopeAll')}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t('coupons.summary', {
                            discount: formatPercent(c.discountPercent),
                            usage: c.maxUses != null
                              ? `${c.usageCount}/${c.maxUses}`
                              : String(c.usageCount),
                          })}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={isPending}
                          onClick={() => openCouponDialog(c)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={isPending}
                          onClick={() => setDeleteCoupon(c)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
        </div>
      </TabsContent>

      {/* ── Certificate template ─────────────────────────── */}
      <TabsContent value="certificate" className="mt-4">
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-sm text-muted-foreground">
              {t('certificate.subtitle')}
            </p>
          </div>

          {templateDataUrl
            ? (
                <div className="flex flex-col gap-3 rounded-lg border border-border/60 p-4">
                  <div className="overflow-hidden rounded-md border border-border/60 bg-muted/40">
                    <img
                      src={templateDataUrl}
                      alt={templateName ?? 'Certificate template preview'}
                      className="w-full"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {templateName ?? t('certificate.unnamed')}
                      </p>
                      <p className="text-xs text-muted-foreground">{t('certificate.activeHint')}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => templateInputRef.current?.click()}
                      >
                        <Upload className="size-4" />
                        {t('certificate.replace')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={onRemoveTemplate}
                      >
                        <Trash2 className="size-4" />
                        {t('certificate.remove')}
                      </Button>
                    </div>
                  </div>
                </div>
              )
            : (
                <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border/70 px-4 py-12 text-center">
                  <Award className="size-10 text-muted-foreground/60" />
                  <div className="space-y-1">
                    <p className="font-medium text-foreground">{t('certificate.emptyTitle')}</p>
                    <p className="text-sm text-muted-foreground">{t('certificate.emptySubtitle')}</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => templateInputRef.current?.click()}
                  >
                    <Upload className="size-4" />
                    {t('certificate.upload')}
                  </Button>
                </div>
              )}

          {templateError && (
            <p className="text-xs text-destructive">{templateError}</p>
          )}

          <input
            ref={templateInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={onTemplateFile}
          />
        </div>
      </TabsContent>

      {/* ── Add category dialog ──────────────────────────── */}
      <Dialog
        open={addCatOpen}
        onOpenChange={(open) => {
          setAddCatOpen(open)
          if (!open)
            setAddCatName('')
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('categories.addDialog.title')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onAddCategory} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="addCatName">{t('categories.addDialog.nameLabel')}</Label>
              <Input
                id="addCatName"
                required
                autoFocus
                value={addCatName}
                onChange={e => setAddCatName(e.target.value)}
                placeholder={t('categories.addDialog.namePlaceholder')}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddCatOpen(false)}>
                {tCommon('cancel')}
              </Button>
              <Button type="submit" disabled={isPending || !addCatName.trim()}>
                {isPending ? t('categories.addDialog.submitting') : t('categories.addDialog.submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Rename category dialog ───────────────────────── */}
      <Dialog
        open={!!renameCat}
        onOpenChange={(open) => {
          if (!open)
            setRenameCat(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('categories.renameDialog.title')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onRenameCategory} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="renameCatName">{t('categories.renameDialog.nameLabel')}</Label>
              <Input
                id="renameCatName"
                required
                autoFocus
                value={renameCatName}
                onChange={e => setRenameCatName(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameCat(null)}>
                {tCommon('cancel')}
              </Button>
              <Button type="submit" disabled={isPending || !renameCatName.trim()}>
                {isPending ? t('categories.renameDialog.submitting') : t('categories.renameDialog.submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Delete category dialog ───────────────────────── */}
      <Dialog
        open={!!deleteCat}
        onOpenChange={(open) => {
          if (!open)
            setDeleteCat(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('categories.deleteDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('categories.deleteDialog.description', { name: deleteCat?.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCat(null)}>
              {tCommon('cancel')}
            </Button>
            <Button variant="outline" className="text-destructive hover:text-destructive" disabled={isPending} onClick={onDeleteCategory}>
              <Trash2 className="size-4" />
              {t('categories.deleteDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Coupon add/edit dialog ───────────────────────── */}
      <Dialog
        open={couponDialogOpen}
        onOpenChange={(open) => {
          setCouponDialogOpen(open)
          if (!open) {
            setEditingCoupon(null)
            setCouponForm(EMPTY_COUPON_FORM)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCoupon ? t('coupons.editDialog.title') : t('coupons.addDialog.title')}
            </DialogTitle>
            <DialogDescription>
              {editingCoupon
                ? t('coupons.editDialog.description')
                : t('coupons.addDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmitCoupon} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="couponCode">{t('coupons.fields.code')}</Label>
              <Input
                id="couponCode"
                required
                autoFocus
                value={couponForm.code}
                onChange={e =>
                  setCouponForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder={t('coupons.fields.codePlaceholder')}
                className="font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="couponDiscount">{t('coupons.fields.discountPercent')}</Label>
                <Input
                  id="couponDiscount"
                  required
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={couponForm.discountPercent}
                  onChange={e =>
                    setCouponForm(p => ({ ...p, discountPercent: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="couponMaxUses">{t('coupons.fields.maxUses')}</Label>
                <Input
                  id="couponMaxUses"
                  type="number"
                  min="1"
                  value={couponForm.maxUses}
                  onChange={e => setCouponForm(p => ({ ...p, maxUses: e.target.value }))}
                  placeholder={t('coupons.fields.maxUsesPlaceholder')}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t('coupons.fields.scope')}</Label>
              <Select
                value={couponForm.scope}
                onValueChange={v =>
                  setCouponForm(p => ({
                    ...p,
                    scope: v as 'all' | 'course',
                    courseId: v === 'all' ? '' : p.courseId,
                  }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('coupons.scopeAll')}</SelectItem>
                  <SelectItem value="course">{t('coupons.scopeCourse')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {couponForm.scope === 'course' && (
              <div className="space-y-1.5">
                <Label>{t('coupons.fields.course')}</Label>
                {orgCourses.length === 0
                  ? (
                      <p className="text-xs text-muted-foreground">
                        {t('coupons.fields.noCourses')}
                      </p>
                    )
                  : (
                      <Select
                        value={couponForm.courseId || undefined}
                        onValueChange={v => setCouponForm(p => ({ ...p, courseId: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('coupons.fields.coursePlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {orgCourses.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
              </div>
            )}
            <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
              <Label htmlFor="couponActive" className="m-0 cursor-pointer">
                {t('coupons.fields.active')}
              </Label>
              <Switch
                id="couponActive"
                checked={couponForm.active}
                onCheckedChange={v => setCouponForm(p => ({ ...p, active: v }))}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCouponDialogOpen(false)}
              >
                {tCommon('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={
                  isPending
                  || !couponForm.code.trim()
                  || !couponForm.discountPercent
                  || (couponForm.scope === 'course' && !couponForm.courseId)
                }
              >
                {isPending
                  ? t('coupons.addDialog.submitting')
                  : editingCoupon
                    ? t('coupons.editDialog.submit')
                    : t('coupons.addDialog.submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Delete coupon dialog ─────────────────────────── */}
      <Dialog
        open={!!deleteCoupon}
        onOpenChange={(open) => {
          if (!open)
            setDeleteCoupon(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('coupons.deleteDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('coupons.deleteDialog.description', { code: deleteCoupon?.code ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCoupon(null)}>
              {tCommon('cancel')}
            </Button>
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              disabled={isPending}
              onClick={onDeleteCoupon}
            >
              <Trash2 className="size-4" />
              {t('coupons.deleteDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
